import { isForeignForeignAirLeg } from "./usInternationalAirLeg";
import type { FlightLeg } from "./types";

/**
 * People always treated as crew for **domestic segment** passenger excise (not counted as taxable pax).
 * Match is case-insensitive on the full name after trimming. Add or remove names here as your roster changes.
 */
export const CREW_FULL_NAMES_EXCISE: readonly string[] = [
  "Austin Robinson",
  "Connor Maxwell",
];

/**
 * First flight date (YYYY-MM-DD) on which Francis is **not** auto-treated as crew for domestic
 * segment counting (string compare). Last crew day is the calendar day before this. Edit if your
 * situation differs.
 */
export const FRANCIS_FIRST_DATE_NOT_CREW_FOR_EXCISE = "2026-03-22";

const FRANCIS_NORMALIZED = "francis dunleavy";

const CREW_ALWAYS_NORMALIZED = new Set(
  CREW_FULL_NAMES_EXCISE.map((n) => normalizePassengerNameKey(n))
);

/** Strip trailing “ — note” / role suffixes ForeFlight or the worksheet may append. */
export function stripPassengerNameSuffix(raw: string): string {
  const t = raw.trim();
  const m = t.match(/^(.+?)\s*[—–-]\s*.+$/u);
  return m ? m[1]!.trim() : t;
}

/** Lowercase, collapse spaces — used for crew matching and deduping. */
export function normalizePassengerNameKey(raw: string): string {
  return stripPassengerNameSuffix(raw)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function isFrancisCrewOnLeg(flightDate: string): boolean {
  return flightDate < FRANCIS_FIRST_DATE_NOT_CREW_FOR_EXCISE;
}

export function isCrewMemberForExcise(normalizedName: string, flightDate: string): boolean {
  if (!normalizedName) return false;
  if (CREW_ALWAYS_NORMALIZED.has(normalizedName)) return true;
  if (normalizedName === FRANCIS_NORMALIZED) return isFrancisCrewOnLeg(flightDate);
  return false;
}

/**
 * When `passenger_list` has at least one non-empty name token, returns the number of **unique**
 * people who are **not** crew (used for IRS domestic segment passenger fee). Duplicates in the list
 * count once.
 *
 * Returns `null` when there are no name tokens — caller should fall back to `passenger_count` / log.
 */
function parseNameTokens(s: string | null | undefined): string[] {
  return (s ?? "")
    .split(";")
    .map((p) => p.trim())
    .filter(Boolean);
}

export function taxablePassengerCountFromPassengerList(
  passengerList: string | null | undefined,
  flightDate: string
): number | null {
  const parts = parseNameTokens(passengerList);
  if (parts.length === 0) return null;

  const seen = new Set<string>();
  let nonCrew = 0;
  for (const part of parts) {
    const key = normalizePassengerNameKey(part);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    if (!isCrewMemberForExcise(key, flightDate)) nonCrew += 1;
  }
  return nonCrew;
}

/**
 * Domestic segment headcount for a leg: honors optional <code>crewList</code> + <code>passengerList</code>
 * split from the manual form / CSV; otherwise single-list rules.
 */
export function taxablePassengerCountForLeg(leg: FlightLeg): number | null {
  const crewTokens = parseNameTokens(leg.crewList);
  if (crewTokens.length > 0) {
    const crewKeys = new Set(
      crewTokens.map((t) => normalizePassengerNameKey(t)).filter(Boolean)
    );
    const paxTokens = parseNameTokens(leg.passengerList);
    if (paxTokens.length === 0) return 0;

    const seen = new Set<string>();
    let n = 0;
    for (const part of paxTokens) {
      const key = normalizePassengerNameKey(part);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      if (crewKeys.has(key)) continue;
      if (isCrewMemberForExcise(key, leg.flightDate)) continue;
      n += 1;
    }
    return n;
  }
  return taxablePassengerCountFromPassengerList(leg.passengerList, leg.flightDate);
}

/** Short explanation for flight detail / debugging. */
export function taxablePassengerListSummary(leg: FlightLeg): string {
  if (leg.excludeFrom720) return "excluded from Form 720 passenger taxes";
  if (leg.passengersDomesticSegmentTax !== null) {
    return `using ${leg.passengersDomesticSegmentTax} domestic segment passengers (dom_pax override on this leg)`;
  }
  if (isForeignForeignAirLeg(leg.fromIcao, leg.toIcao)) {
    return "foreign–foreign: 0 domestic segment passengers (passenger_count ignored; set dom_pax to override)";
  }
  return `passenger_count (${leg.passengerCount})`;
}
